import type { User } from '@sf/api';

/** What one read of `GET /me` established about the signed-in customer. */
export interface SessionSnapshot {
  status: 'authenticated' | 'anonymous';
  user: User | null;
}

/** The part of the API client this needs — and, deliberately, no way to write to the token store. */
export interface SessionSource {
  isAuthenticated(): boolean;
  me: { get(): Promise<User> };
}

/**
 * Reads the signed-in customer, and decides what it means when that read fails.
 *
 * The rule: **a request that never reached the server is not an answer, and never ends a session.** Only the
 * server can end one, and the API client is the single place that hears it say so — it clears
 * `sf.customer.tokens` itself on a definitive 401 and when a refresh is refused (`http.ts`). So this asks the
 * client back (`isAuthenticated()`) instead of judging the error, and it never writes to the token store.
 *
 * It used to write: `catch { api.setTokens(null) }` treated every failure as a sign-out, and the browser
 * hands out that failure for free — it cancels a page's in-flight requests the moment a hard navigation
 * starts, and the cancelled fetch rejects with `TypeError: Failed to fetch`, indistinguishable here from a
 * real one. Two ways real customers reached it:
 *
 *   offline   the installed app opens from the precached shell on a train, exactly as intended
 *             (`pwa/OfflineNotice.tsx`) — the profile read cannot leave the device, and the customer was
 *             signed out of a session that was never over. Back on the network, the password again.
 *   a reload  sign in, or open the app, and reload or type an address before the profile read has answered.
 *             The window is precisely how long that one request is in flight: 15 ms on a desk, seconds on a
 *             bad mobile link. The dying page deleted the tokens on its way out, so the page that came up
 *             was signed out. `invoice.spec.ts` and `withdrawal.spec.ts` do it deliberately with `page.goto`.
 *
 * Status is unchanged on purpose: a customer whose profile could not be read still sees the sign-in page.
 * What changes is that their session survives it, so coming back online — or simply trying again — costs
 * them nothing.
 */
export async function readSession(api: SessionSource): Promise<SessionSnapshot> {
  if (!api.isAuthenticated()) return { status: 'anonymous', user: null };
  try {
    return { status: 'authenticated', user: await api.me.get() };
  } catch {
    return { status: 'anonymous', user: null };
  }
}
